import {
  Alert,
  AutoComplete,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Tag,
  Typography,
  message,
} from "antd";
import {
  IdcardOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import PhoneInput from "../../../components/PhoneInput";

const { Text } = Typography;

export type ProposalKind = "remove" | "edit" | "new";

interface ContactChainEntry {
  id: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  position_name: string | null;
  priority: number;
  target: "license" | "farm";
  farm: number | null;
}

interface ContactProposalModalProps {
  visible: boolean;
  kind: ProposalKind;
  auditId: number;
  auditReference: string;
  producerId: number;
  producerName: string;
  farmId: number | null;
  farmName: string | null;
  contact?: ContactChainEntry | null;
  onClose: () => void;
  onSubmitted?: () => void;
}

/**
 * Modal used by the audit-detail Contacts panel to file a ticket on
 * behalf of the auditor / coordinator. The admin will review and apply
 * the change manually from `/admin-tickets`.
 *
 * One ticket per proposal. Body is structured HTML (with a hidden
 * <pre data-proposal=…> tag) so a future "apply automatically" feature
 * can parse it back without a schema change.
 */
export const ContactProposalModal = ({
  visible,
  kind,
  auditId,
  auditReference,
  producerId,
  producerName,
  farmId,
  farmName,
  contact,
  onClose,
  onSubmitted,
}: ContactProposalModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  // Resolved when the modal first opens; cached for subsequent opens.
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [resolvingCategory, setResolvingCategory] = useState(false);
  // Business positions are loaded once for the position AutoComplete.
  // Stored as the option shape antd expects (`{value: name}`) so the
  // value in the form is the label itself — we don't need ids because
  // the admin will create/match the position when applying the ticket.
  const [positionOptions, setPositionOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (!visible || categoryId != null) return;
    let cancelled = false;
    (async () => {
      setResolvingCategory(true);
      try {
        const res = await apiClient.get("/v1/ticket-categories/?page_size=100");
        const list: any[] =
          res.data?.results ?? res.data?.data ?? res.data ?? [];
        const found = list.find(
          (c) => (c.name || "").toLowerCase() === "contactos",
        );
        if (!cancelled && found) setCategoryId(found.id);
      } catch {
        // Falls back to the generic category below.
      } finally {
        if (!cancelled) setResolvingCategory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, categoryId]);

  // Load business positions lazily on first open; cached for the
  // session. Failure is silent — the AutoComplete still lets the user
  // type a free-text position so the proposal isn't blocked.
  useEffect(() => {
    if (!visible || positionOptions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(
          "/business-positions/?page_size=500&ordering=name",
        );
        const list: any[] =
          res.data?.results ?? res.data?.data ?? res.data ?? [];
        if (cancelled) return;
        const opts = list
          .map((p: any) => ({ value: p.name, label: p.name }))
          .sort((a, b) => a.value.localeCompare(b.value));
        setPositionOptions(opts);
      } catch {
        if (!cancelled) setPositionOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, positionOptions.length]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      return;
    }
    if (kind === "edit" && contact) {
      form.setFieldsValue({
        name: contact.contact_name,
        phone: contact.contact_phone,
        email: contact.contact_email,
        position: contact.position_name || "",
        reason: "",
      });
    } else if (kind === "new") {
      form.setFieldsValue({
        name: "",
        phone: "",
        email: "",
        position: "",
        reason: "",
      });
    } else {
      form.setFieldsValue({ reason: "" });
    }
  }, [visible, kind, contact, form]);

  const title = useMemo(() => {
    if (kind === "remove")
      return t("contacts.propose_remove_title", "Proponer baja de contacto");
    if (kind === "edit")
      return t("contacts.propose_edit_title", "Proponer edición de contacto");
    return t("contacts.propose_new_title", "Proponer nuevo contacto");
  }, [kind, t]);

  const scopeLabel = farmName
    ? `${t("audit.farm", "Finca")} · ${farmName}`
    : t("contacts.scope_license", "Licencia");

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const proposal: Record<string, any> = {
        kind,
        audit_id: auditId,
        audit_reference: auditReference,
        producer_id: producerId,
        producer_name: producerName,
        farm_id: farmId,
        farm_name: farmName,
        contact_id: contact?.id || null,
        contact_name: contact?.contact_name || null,
        contact_phone: contact?.contact_phone || null,
        contact_email: contact?.contact_email || null,
        reason: (values.reason || "").trim(),
      };
      if (kind === "edit" || kind === "new") {
        proposal.proposed = {
          name: (values.name || "").trim(),
          phone: (values.phone || "").trim(),
          email: (values.email || "").trim(),
          position: (values.position || "").trim(),
        };
      }

      const subject = (() => {
        const ref = auditReference || `#${auditId}`;
        if (kind === "remove")
          return `[${ref}] Baja contacto · ${contact?.contact_name ?? "—"}`;
        if (kind === "edit")
          return `[${ref}] Editar contacto · ${contact?.contact_name ?? "—"}`;
        return `[${ref}] Nuevo contacto · ${producerName}`;
      })();

      const proposalJson = JSON.stringify(proposal, null, 2);
      const escapeHtml = (s: string) =>
        s.replace(/[&<>]/g, (c) =>
          c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;",
        );

      const summary =
        kind === "remove"
          ? `<p>Solicito dar de baja al contacto <strong>${escapeHtml(
              contact?.contact_name ?? "—",
            )}</strong> de la cadena <strong>${escapeHtml(scopeLabel)}</strong> del productor <strong>${escapeHtml(
              producerName,
            )}</strong>.</p>`
          : kind === "edit"
            ? `<p>Solicito actualizar los datos del contacto <strong>${escapeHtml(
                contact?.contact_name ?? "—",
              )}</strong> del productor <strong>${escapeHtml(
                producerName,
              )}</strong>.</p>`
            : `<p>Solicito dar de alta un nuevo contacto en la cadena <strong>${escapeHtml(
                scopeLabel,
              )}</strong> del productor <strong>${escapeHtml(
                producerName,
              )}</strong>.</p>`;

      const proposedBlock =
        kind === "edit" || kind === "new"
          ? `<h4>Datos propuestos</h4>
             <ul>
               <li><strong>Nombre:</strong> ${escapeHtml(proposal.proposed.name || "—")}</li>
               <li><strong>Teléfono:</strong> ${escapeHtml(proposal.proposed.phone || "—")}</li>
               <li><strong>Email:</strong> ${escapeHtml(proposal.proposed.email || "—")}</li>
               <li><strong>Cargo:</strong> ${escapeHtml(proposal.proposed.position || "—")}</li>
             </ul>`
          : "";

      const reasonBlock = proposal.reason
        ? `<h4>Motivo</h4><p>${escapeHtml(proposal.reason)}</p>`
        : "";

      const auditLink = `<p><a href="/audits/${auditId}" target="_blank">Auditoría ${escapeHtml(auditReference)}</a></p>`;

      const body_html =
        summary +
        proposedBlock +
        reasonBlock +
        auditLink +
        // Machine-readable copy so a future bot can re-parse it.
        `<pre data-proposal="1" style="display:none">${escapeHtml(proposalJson)}</pre>`;

      const payload: Record<string, any> = {
        subject,
        body_html,
      };
      if (categoryId != null) payload.category = categoryId;

      await apiClient.post("/v1/tickets/", payload);
      message.success(
        t("contacts.proposal_sent", "Propuesta enviada al administrador"),
      );
      onSubmitted?.();
      onClose();
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t("contacts.proposal_error", "No se pudo enviar la propuesta");
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("contacts.send_proposal", "Enviar propuesta")}
      cancelText={t("common.cancel", "Cancelar")}
      confirmLoading={submitting || resolvingCategory}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t(
          "contacts.proposal_help",
          "Se creará un ticket de soporte para que el administrador revise y aplique el cambio.",
        )}
      />
      <div style={{ marginBottom: 10 }}>
        <Tag color="blue">{scopeLabel}</Tag>
        {contact && (
          <Tag color="default" style={{ marginLeft: 6 }}>
            #{contact.priority} · {contact.contact_name || "—"}
          </Tag>
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark="optional"
      >
        {(kind === "edit" || kind === "new") && (
          <>
            <Divider orientation="left" plain style={{ marginTop: 0 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("contacts.section_personal", "Datos personales")}
              </Text>
            </Divider>

            <Row gutter={16}>
              <Col xs={24} sm={14}>
                <Form.Item
                  name="name"
                  label={t("contacts.name", "Nombre")}
                  rules={[
                    {
                      required: true,
                      message: t(
                        "contacts.name_required",
                        "Nombre obligatorio",
                      ),
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                    maxLength={200}
                    placeholder={t(
                      "contacts.name_placeholder",
                      "Nombre y apellidos del contacto",
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10}>
                <Form.Item
                  name="position"
                  label={t("contacts.position", "Cargo")}
                >
                  <AutoComplete
                    options={positionOptions}
                    placeholder={t(
                      "contacts.position_placeholder",
                      "Ej. Gerente, Propietario…",
                    )}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    allowClear
                    getPopupContainer={(trigger) =>
                      (trigger?.parentElement as HTMLElement) ?? document.body
                    }
                  >
                    <Input
                      prefix={<IdcardOutlined style={{ color: "#bfbfbf" }} />}
                      maxLength={100}
                    />
                  </AutoComplete>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" plain>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("contacts.section_reach", "Cómo contactarle")}
              </Text>
            </Divider>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone"
                  label={t("contacts.phone", "Teléfono")}
                >
                  <PhoneInput
                    placeholder={t(
                      "contacts.phone_placeholder",
                      "Ej. 612 345 678",
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="email"
                  label={t("contacts.email", "Email")}
                  rules={[
                    {
                      type: "email",
                      message: t("common.invalid_email", "Email no válido"),
                    },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                    maxLength={150}
                    placeholder={t(
                      "contacts.email_placeholder",
                      "contacto@empresa.com",
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" plain>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("contacts.section_reason", "Motivo de la propuesta")}
              </Text>
            </Divider>
          </>
        )}

        <Form.Item
          name="reason"
          label={t("contacts.reason", "Motivo")}
          rules={[
            {
              required: true,
              message: t("contacts.reason_required", "Indica el motivo"),
            },
            {
              min: 5,
              message: t(
                "contacts.reason_min",
                "Describe el motivo (mínimo 5 caracteres)",
              ),
            },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={1000}
            showCount
            placeholder={t(
              "contacts.reason_placeholder",
              "Explica brevemente el cambio para que el admin lo valide",
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ContactProposalModal;
